import { createAction } from "redux-actions";
import {
  MATFILE_ENDLESS_TYPE,
  MATFILE_DIECUT_TYPE,
  MATFILE_LASER_TYPE
} from 'pxc-editor-core/constants';
import Section from "pxc-editor-core/entities/Section";
import { contains } from "pxc-editor-core/helpers/common";
import { isMultiColored as isMaterialMultiColored } from "pxc-editor-core/helpers/material";
import { getPageObjects } from "pxc-editor-core/helpers/schema";
import { INTERACTION_MODE } from "pxc-editor-material-svg-rendering/constants";
import {
  ADD_FIRST_PAGE,
  LOAD_PROJECT_FILE,
  ZOOM_OUT_STATE,
  SELECT_COLORS_LIST,
  NO_PRINTER_ID
} from '@/constants';
import { 
  recalcZoomBordersScaleCoefs,
  getWizardData,
  isWebComponentEnvironment,
  isMarkingSystemEnvironment, 
  isPrime2Environment,
  setProjectToStorage,
  createNewPage,
} from '@/helpers';
import { setLaserOutputQualityThunk, setPreselectedPrinter, setPrinterList } from '@/actions';
import { resetState, setMultiColoredState, setSectionsDisplayState, setProjectName, setWizardData, setModeActionThunk } from "./main";
import { handleZoomStateChange } from "./rendererView";
import { setInitColorSchemaThunk } from './colorSchema';
import { setLastSavedPages, setSelectedPage, createCableFlagSections, checkSyncSlaveShieldByMasterShield, switchSelectedPage } from "./page";
import { getGlobalSettingsActionThunk } from '@/store/actions/app-settings';

const addFirstPageAction = (payload) => createAction(ADD_FIRST_PAGE, () => payload)();

const loadProjectFileAction = (payload) => createAction(LOAD_PROJECT_FILE, () => payload)();

export const setSavedProjectName = (file_name) => (dispatch) => {
  if (file_name) {
    dispatch(setProjectName(file_name));
  }
}

export const uploadProject = (data) => (dispatch, getState) => {
  const { projectData, beforeFinishActionsClb = null } = data;
  const { core: { pages } } = getState();
  const { matfileTemplate, project, selectedVariantArticleNumber, file_name, new_settings = null, wizard = null } = projectData;

  dispatch(resetState());

  if (project) {
    dispatch(resetProjectFromProject(project, matfileTemplate, file_name, new_settings));
    if (wizard?.application_type === 'CABLE_FLAG') {
      const wizardProjectData = getWizardData(wizard.data);
      wizard['data'] = wizardProjectData;
      dispatch(setWizardData(wizard));
      if (pages && Object.values(pages).length > 0) {
        Object.values(pages).forEach(page => {
          const pageUid = page.uid;
          dispatch(switchSelectedPage(pageUid));
          const { shields: targetShields } = getPageObjects(pages[pageUid]);
          const shieldsArray = targetShields? Object.values(targetShields).sort((a,b) => a.primitiveProps.x - b.primitiveProps.x) : [];
          if (shieldsArray?.length === 2) {
            dispatch(checkSyncSlaveShieldByMasterShield(shieldsArray[0]));
          }
        });
      }
    }
  } else {
    if (wizard?.application_type === 'CABLE_FLAG') {
      const wizardProjectData = getWizardData(wizard.data);
      wizard['data'] = wizardProjectData;
      dispatch(setWizardData(wizard));
      dispatch(setModeActionThunk(INTERACTION_MODE.WIZARD));
      dispatch(resetProjectFromMatfile(matfileTemplate, selectedVariantArticleNumber));
    } else {
      dispatch(resetProjectFromMatfile(matfileTemplate, selectedVariantArticleNumber));
    }
  }

  if (projectData?.laserOutputQuality) {
    dispatch(setLaserOutputQualityThunk(projectData.laserOutputQuality, false));
  }

  if (projectData?.selPrinter >= NO_PRINTER_ID) {
    dispatch(setPreselectedPrinter(projectData.selPrinter));
  }

  if (projectData?.printerList) {
    dispatch(setPrinterList(projectData.printerList));
  }

  dispatch(setLastSavedPages());

  recalcZoomBordersScaleCoefs();

  dispatch(handleZoomStateChange(ZOOM_OUT_STATE));

  if (typeof beforeFinishActionsClb === "function") {
    beforeFinishActionsClb();
  }

  dispatch(setProjectToStorage());
};

const resetProjectFromMatfile = (matfileTemplate, selectedVariantArticleNumber = null) => (dispatch, getState) => {
  const state = getState();
  const { core: { selWizard, platform } } = state;
  const { type, variants, printParameters, name } = matfileTemplate;
  const webComponent = isWebComponentEnvironment();
  const markingSystemEnvironment = isMarkingSystemEnvironment(platform.config.environment);
  const prime2Environment = isPrime2Environment(platform.config.environment);

  dispatch(setMultiColoredState(isMaterialMultiColored(printParameters)));

  if (variants?.length > 0) {
    const selectedVariant = variants.find(variant => variant.article_number === selectedVariantArticleNumber) || variants[0];
    dispatch(setInitColorSchemaThunk(selectedVariant));
  }

  if (name) {
    dispatch(setProjectName(name));
  }

  if (!webComponent && !markingSystemEnvironment && !prime2Environment) {
    const sectionsDisplayState = type !== MATFILE_ENDLESS_TYPE && type !== MATFILE_DIECUT_TYPE && type !== MATFILE_LASER_TYPE;
    dispatch(setSectionsDisplayState(sectionsDisplayState));
  }

  const page = createNewPage();

  dispatch(addFirstPageAction(page));
  dispatch(setSelectedPage({ pageUid: page.uid }));
  
  if (selWizard) {
    dispatch(createCableFlagSections(page.uid));
  }
};

const resetProjectFromProject = (project, matfileTemplate, file_name, new_settings) => (dispatch, getState) => {
  const { core: { isMultiColored, platform } } = getState();
  const { pages: rawPages, colorSchema, usableColors = null, globalProjectSettings } = project;
  const fontSettings = new_settings?.fontSettings? new_settings.fontSettings : null;
  const webComponent = isWebComponentEnvironment();
  const markingSystemEnvironment = isMarkingSystemEnvironment(platform.config.environment);
  const prime2Environment = isPrime2Environment(platform.config.environment);

  dispatch(setMultiColoredState(matfileTemplate.printParameters.color_output_mode !== 'MONO'));
  dispatch(getGlobalSettingsActionThunk(globalProjectSettings));

  if (colorSchema) {
    dispatch(setInitColorSchemaThunk(colorSchema));
  }

  if (file_name) {
    dispatch(setProjectName(file_name));
  } else if (matfileTemplate.name) {
    dispatch(setProjectName(matfileTemplate.name));
  }

  if (!webComponent && !markingSystemEnvironment && !prime2Environment) {
    const sectionsDisplayState = matfileTemplate.type !== MATFILE_ENDLESS_TYPE && matfileTemplate.type !== MATFILE_DIECUT_TYPE && matfileTemplate.type !== MATFILE_LASER_TYPE;
    dispatch(setSectionsDisplayState(sectionsDisplayState));
  }

  let customColors = null;
  if (usableColors) {
    const { fontColors } = usableColors;
    const existColors = SELECT_COLORS_LIST.slice().concat([paperColor, ribbonColor]);
    customColors = {
      customFontColors: fontColors.filter(color => !contains(color, existColors)),
    };
  }

  const { core: { paperColor, ribbonColor } } = getState();

  let firstPage = null;
  let pages = {};
  const pagesOrder = [];
  for (let pageUid in rawPages) {
    const rawPage = rawPages[pageUid];
    let page = { ...rawPage };
    for (let secId in rawPage.sections) {
      const rawSec = rawPage.sections[secId];
      const secInst = Section.fromDict(rawSec);

      if (!isMultiColored) {
        const corrRibbonColor = (secInst.fillColor === paperColor) ? ribbonColor : paperColor;
        secInst
          .setFontColor(corrRibbonColor)
          .setGlyphFillColor(corrRibbonColor)
          .setBarcodeColor(corrRibbonColor);
      } else {
        if (fontSettings) {
          if (!!fontSettings.font) secInst.setFont(fontSettings.font);
          if (!!fontSettings.isBold) secInst.setBold(fontSettings.isBold === true);
          if (!!fontSettings.isItalic) secInst.setItalic(fontSettings.isItalic === true);
          if (!!fontSettings.isUnderline) secInst.setUnderline(fontSettings.isUnderline === true);
          if (!!fontSettings.fontSize && !isNaN(fontSettings.fontSize) && fontSettings.fontSize > 0) secInst.setFontSize(fontSettings.fontSize);
          if (!!fontSettings.fontColor) secInst.setFontColor(fontSettings.fontColor)
          if (!!fontSettings.horizontalAlignment) secInst.setHorizontalAlignment(fontSettings.horizontalAlignment)
          if (!!fontSettings.verticalAlignment) secInst.setVerticalAlignment(fontSettings.verticalAlignment)
          if (!!fontSettings.angle && !isNaN(fontSettings.angle)) secInst.setAngle(fontSettings.angle)
        }
      }
      page.sections[secId] = secInst.toDict();
    }
    pages[pageUid] = page;
    pagesOrder.push(pageUid);
    if (!firstPage && page) {
      firstPage = page;
    }
  }

  dispatch(loadProjectFileAction({ pages, pagesOrder, ...customColors }));

  if (!firstPage && pages && Object.values(pages).length) {
    firstPage = Object.values(pages)[0];
  }

  if (firstPage) {
    dispatch(setSelectedPage({ pageUid: firstPage.uid }));
  }
};
